import React, { useState } from "react";
import { useHistory, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Heading,
  Text,
  Image,
  Flex,
  Stack,
  Link,
  VStack,
  IconButton,
  HStack,
  Input,
  InputGroup,
  Button,
  useToast, // Chakra's toast for notifications
  useDisclosure,
  Divider,
  FormControl,
  FormErrorMessage,
  SimpleGrid,
  Link as ChakraLink,
} from "@chakra-ui/react";
import axiosInstance from "../helpers/axiosInstance"; // Import Axios instance for API calls
import logo from "../assets/images/New-Business-Logo.png";
import { FaInstagram, FaYoutube, FaTiktok, FaLinkedin } from "react-icons/fa";
import { EmailIcon } from "@chakra-ui/icons";
import profilePicture from "../assets/images/Ava.png";
// import { ReactComponent as LinktreeIcon } from "../assets/icons/linktree_logo_icon.svg";
import { SurveyPopup } from "./Popups";
import { useTranslation } from "../components/LanguageTranslation/TranslationContext";
import Translate from "../components/LanguageTranslation/Translate";
import { validateEmail } from "../utils/validation";
import MobileSocialIcons from "./common/MobileSocialIcons";

const Footer = () => {
  const history = useHistory();
  const { useTranslate } = useTranslation();
  const [email, setEmail] = useState(""); // State for storing email input
  const [isTouched, setIsTouched] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const showEmailError = (isTouched || isSubmitted) && !validateEmail(email);

  const toast = useToast(); // For notifications
  const {
    isOpen: isSurveyOpen,
    onOpen: onOpenSurvey,
    onClose: onCloseSurvey,
  } = useDisclosure(); // Renamed this for clarity

  const handleSurveyPopupClose = () => {
    onCloseSurvey(); // Use the correct close function from `useDisclosure`
  };

  const handleLinkClick = (message) => {
    localStorage.removeItem("conversation_id"); // Clear any existing conversation
    history.push(
      `/ai-travel-planner/chat?message=${encodeURIComponent(message)}`
    ); // Redirect to the chat system with the clicked text
  };

  const handleSubscribe = async () => {
    setIsSubmitted(true);

    if (!validateEmail(email)) {
      return; // Stop if email is invalid
    }

    if (!email) {
      toast({
        title: "Email Required",
        description: "Please enter a valid email address.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const response = await axiosInstance.post("/newsletter/", { 
        email, 
        is_manual: true,
        date: new Date().toISOString()
      });
      setIsSubmitted(false);
      if (response.status === 201) {
        toast({
          title: "Subscribed!",
          description: "You've successfully subscribed to the newsletter.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        setEmail(""); // Clear the email input after successful submission
      }
    } catch (error) {
      setIsSubmitted(false);
      toast({
        title: "Error",
        description:
          error.response.data.message || "An error occurred. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const travelAdventureText = useTranslate("Plan a Travel Adventure");
  const campingTripText = useTranslate("Plan a Camping Trip");
  const roadTripText = useTranslate("Plan a Road Trip Adventure");
  const winterAdventureText = useTranslate(
    "Plan a Winter Adventure & Ski Trip"
  );
  const hikingExpeditionText = useTranslate("Plan a Hiking Expedition");
  const backpackingText = useTranslate("Plan a Backpacking Trip");

  return (
    <Box p={{ base: 6, md: 8 }} borderTop="1px" borderColor="blackAlpha.100">
      <Flex
        justifyContent="space-between"
        alignItems="start"
        direction={{ base: "row", md: "row" }}
        wrap="wrap"
      >
        <Box flex={{ base: "2", md: "2", lg: "1.5" }} mb={{ base: 2, md: 0 }}>
          <Link as={RouterLink} to="/home" borderRadius="xl" bg="white">
            <Image
              src={logo}
              alt="NOMAD360"
              mb={5}
              width={{ base: "100px", md: "120px", lg: "120px" }}
              height={{ base: "auto", md: "25px", lg: "25px" }}
            />
          </Link>
          <Text
            color="#4B5563"
            w={{ base: "95%", md: "80%" }}
            fontSize={{ base: "sm", md: "sm", lg: "md" }}
          >
            <Text as="span" fontWeight="bold">
              NOMAD360™
            </Text>{" "}
            <Translate
              text="creates personalized travel plans in seconds, tailored to your
            outdoor lifestyle. Stress-free planning and epic adventures await!"
            />
          </Text>
          <MobileSocialIcons />
        </Box>

        <Box
          display={{ base: "none", md: "block" }}
          flex={{ base: "1", md: "2", lg: "1" }}
          mb={{ base: 8, md: 0 }}
        >
          <Heading
            color="#374155"
            as="h4"
            size={{ base: "md", md: "sm", lg: "md" }}
            mb={4}
          >
            <Translate text="Resources" />
          </Heading>
          <Stack>
            <Link
              color="#4B5563"
              fontSize={{ base: "md", md: "sm", lg: "md" }}
              href="/blogs"
            >
              <Translate text="Blogs" />
            </Link>
            <Link
              color="#4B5563"
              fontSize={{ base: "md", md: "sm", lg: "md" }}
              href="/offset-your-carbon-footprint"
            >
              <Translate text="Offset your Carbon Footprint" />
            </Link>
            <Link
              color="#4B5563"
              fontSize={{ base: "md", md: "sm", lg: "md" }}
              onClick={onOpenSurvey}
            >
              <Translate text="Give us Feedback" />
            </Link>
            <Link
              color="#4B5563"
              fontSize={{ base: "md", md: "sm", lg: "md" }}
              href="/policies/terms-of-service"
            >
              <Translate text="Terms of Service" />
            </Link>
            <Link
              color="#4B5563"
              fontSize={{ base: "md", md: "sm", lg: "md" }}
              href="/policies/privacy-policy"
            >
              <Translate text="Privacy Policy" />
            </Link>
            {/* <Link color="#4B5563" href="/disclaimer">
              <Translate text="Disclaimer" />
            </Link> */}
          </Stack>
          <SimpleGrid w={{ md:"100%", lg: "80%", xl: "60%" }} columns={{ md: 4, lg: 4, xl: 5 }} spacing={2} mt={3}>
            <IconButton
              as={Link}
              href="https://mail.google.com/mail/?view=cm&fs=1&to=info@nomad360.io"
              aria-label="contact us"
              icon={<EmailIcon />}
              isRound
              bg="blackAlpha.100"
              // mt={2}
              target="_blank"
              rel="noopener noreferrer"
            />
            <IconButton
              aria-label="TikTok"
              icon={<FaTiktok />}
              isRound
              bg="blackAlpha.100"
              // mt={2}
              as="a"
              href="https://www.tiktok.com/@nomad360.io"
              target="_blank"
              rel="noopener noreferrer"
            />
            <IconButton
              aria-label="Instagram"
              icon={<FaInstagram />}
              isRound
              bg="blackAlpha.100"
              // mt={2}
              as="a"
              href="https://www.instagram.com/nomad360.io/"
              target="_blank"
              rel="noopener noreferrer"
            />
            <IconButton
              aria-label="YouTube"
              icon={<FaYoutube />}
              isRound
              bg="blackAlpha.100"
              // mt={2}
              as="a"
              href="https://youtube.com/@nomad360io"
              target="_blank"
              rel="noopener noreferrer"
            />
            <IconButton
              aria-label="LinkedIn"
              icon={<FaLinkedin />}
              isRound
              bg="blackAlpha.100"
              // mt={2}
              as="a"
              href="https://www.linkedin.com/showcase/nomad360"
              target="_blank"
              rel="noopener noreferrer"
            />
          </SimpleGrid>
        </Box>

        {/* Get started section */}
        <Box flex={{ base: "2", md: "2", lg: "1.5" }} mb={{ base: 8, md: 0 }}>
          <Heading
            color="#374155"
            as="h4"
            size={{ base: "md", md: "sm", lg: "md" }}
            mb={4}
          >
            <Translate text="Get started" />
          </Heading>
          <Stack>
            <Link
              color="#4B5563"
              fontSize={{ base: "sm", md: "sm", lg: "md" }}
              onClick={() => handleLinkClick(travelAdventureText)}
            >
              {travelAdventureText}
            </Link>
            <Link
              color="#4B5563"
              fontSize={{ base: "sm", md: "sm", lg: "md" }}
              onClick={() => handleLinkClick(campingTripText)}
            >
              {campingTripText}
            </Link>
            <Link
              color="#4B5563"
              fontSize={{ base: "sm", md: "sm", lg: "md" }}
              onClick={() => handleLinkClick(roadTripText)}
            >
              {roadTripText}
            </Link>
            <Link
              color="#4B5563"
              fontSize={{ base: "sm", md: "sm", lg: "md" }}
              onClick={() => handleLinkClick(winterAdventureText)}
            >
              {winterAdventureText}
            </Link>
            <Link
              color="#4B5563"
              fontSize={{ base: "sm", md: "sm", lg: "md" }}
              onClick={() => handleLinkClick(hikingExpeditionText)}
            >
              {hikingExpeditionText}
            </Link>
            <Link
              color="#4B5563"
              fontSize={{ base: "sm", md: "sm", lg: "md" }}
              onClick={() => handleLinkClick(backpackingText)}
            >
              {backpackingText}
            </Link>
          </Stack>
        </Box>

        {/* Newsletter Subscription Section */}
        <Box
          display={{ base: "none", md: "flex" }}
          flex={{ base: "1.5", md: "2", lg: "1.5" }}
        >
          <VStack alignItems="left" spacing={3} width="100%">
            <HStack alignItems="center" spacing={4} mb={2}>
              <Image
                src={profilePicture}
                alt="Ask Ava"
                borderRadius="full"
                border="2px solid"
                borderColor="black"
                objectFit="cover"
                objectPosition="top"
                width={{ base: "40px", md: "95px", lg: "75px", xl: "100px" }}
                height={{ base: "40px", md: "45px", lg: "65px", xl: "100px" }}
              />
              <Heading
                color="#374155"
                as="h4"
                size={{ base: "md", md: "sm", lg: "md" }}
                mb={0}
              >
                <Translate text="Join the Green & Wild Community!" />
              </Heading>
            </HStack>
            <Text
              color="#4B5563"
              fontSize={{ base: "md", md: "sm", lg: "md" }}
              mb={0}
            >
              <Translate
                text="Subscribe to NOMAD360' newsletter for personalized travel tips,
              epic outdoor experiences, and exclusive offers."
              />
            </Text>
            <FormControl isInvalid={showEmailError}>
              <InputGroup mt={2} width="100%" maxWidth="400px">
                <Input
                  type="email"
                  placeholder={Translate({
                    text: "Enter email",
                    as: "string",
                  })}
                  size={{ base: "md", md: "sm", lg: "md" }}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (!isTouched) setIsTouched(true);
                  }}
                  onBlur={() => setIsTouched(false)}
                />
              </InputGroup>
              {showEmailError && (
                <FormErrorMessage>
                  Please enter a valid email address.
                </FormErrorMessage>
              )}
            </FormControl>

            <Button
              bg="black"
              color="white"
              width="100%"
              maxWidth="400px"
              // borderRadius="full"
              size={{ base: "md", md: "sm", lg: "md" }}
              transition="transform 0.3s"
              _hover={{
                transform: "scale(1.1)",
                // border: "2px solid #FFC719",
                // borderColor: "#FFC719",
                bg: "black",
              }}
              onClick={handleSubscribe} // Handle subscription click
            >
              <Translate text="Subscribe" />
            </Button>
          </VStack>
        </Box>
      </Flex>

      <Text
        color="#374155"
        textAlign={{ base: "center", md: "center", lg: "center", xl: "center" }}
        fontSize={{ base: "sm", md: "sm", lg: "sm", xl: "md" }}
        mt={8}
        mb={{ base: 1, md: 0, lg: 0, xl: 0 }}
      >
        <Translate text="This work is governed by the licensing and distribution agreement with " />
        <Link href="https://lancewood.nl" color="blue.500" isExternal>
          Lancewood AI Solutions B.V
        </Link>
        <Translate text="." />
      </Text>
      {/* <Divider bg="blackAlpha.300" display={{ base: "block", md: "none" }} /> */}

      <Text
        color="#374155"
        textAlign={{ base: "center", md: "center", lg: "center", xl: "center" }}
        fontSize={{ base: "sm", md: "sm", lg: "sm", xl: "md" }}
        mb={{ base: 1, md: 0, lg: 0, xl: 0 }}
      >
        NOMAD360™{" "}
        <Translate
          text="is a trademark for which a formal registration application
        has been filed."
        />
      </Text>

      {/* <Divider bg="blackAlpha.300" display={{ base: "block", md: "none" }} /> */}

      <Text
        display={{ base: "none", md: "block" }}
        color="#374155"
        textAlign={{ base: "center", md: "center", lg: "center", xl: "center" }}
        fontSize={{ base: "sm", md: "sm", lg: "sm", xl: "md" }}
        mb={{ base: 1, md: 0, lg: 0, xl: 0 }}
      >
        © 2024-2025 NOMAD360™.{" "}
        <Translate
          text=" All Rights Reserved. Unauthorized distribution,
        duplication, or reproduction of this material is strictly prohibited."
        />
      </Text>
      <Text
        display={{ base: "block", md: "none" }}
        color="#374155"
        textAlign={{ base: "center", md: "center", lg: "center", xl: "center" }}
        fontSize={{ base: "sm", md: "sm", lg: "sm", xl: "md" }}
        mb={{ base: 1, md: 0, lg: 0, xl: 0 }}
      >
        <Translate
          text=" All Rights Reserved. Unauthorized distribution,
        duplication, or reproduction of this material is strictly prohibited."
        />
      </Text>

      <Text
        display={{ base: "block", md: "none" }}
        color="#374155"
        textAlign={{ base: "center", md: "center", lg: "center", xl: "center" }}
        fontSize={{ base: "sm", md: "sm", lg: "sm", xl: "md" }}
        mb={{ base: 1 }}
        mt={6}
      >
        © 2024-2025 NOMAD360™
      </Text>

      <HStack
        display={{ base: "flex", md: "none" }}
        justify="center" // ⬅️ horizontal centering
        spacing={4}
        divider={
          <Divider
            orientation="vertical"
            h="20px" // height of the line
            borderColor="gray.400"
          />
        }
      >
        <Link
          color="#4B5563"
          fontSize={{ base: "sm", md: "sm", lg: "md" }}
          href="/policies/terms-of-service"
        >
          <Translate text="Terms of Service" />
        </Link>

        <Link
          color="#4B5563"
          fontSize={{ base: "sm", md: "sm", lg: "md" }}
          href="/policies/privacy-policy"
        >
          <Translate text="Privacy Policy" />
        </Link>
      </HStack>
      <SurveyPopup
        isOpen={isSurveyOpen}
        onClose={handleSurveyPopupClose} // Use the renamed function here
      />
    </Box>
  );
};

export default Footer;
